// Duenner Client fuer die /billing-Endpoints. Auth via Bearer-Access-Token;
// APISIX setzt daraus den X-ID-Token fuers Backend. Preise/Entitlements kommen
// ausschliesslich vom Server — die App rechnet nichts selbst.
import { CONFIG } from "./config"
import { freshToken } from "./auth"
import type { CartLine, CatalogItem, CheckoutResponse, Me, PaymentMethod } from "./types"

function url(path: string): string {
  return `${CONFIG.apiBase}${path}`
}

async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { "Content-Type": "application/json" }
  const token = await freshToken()
  if (token) headers["Authorization"] = `Bearer ${token}`
  return headers
}

// Warenkorb -> Request-Items. selected_packages nur bei Bundle/Staffel.
function itemsPayload(lines: CartLine[]) {
  return lines.map((l) => ({
    price_id: l.item.price_id,
    quantity: l.quantity,
    // Nur bei Bundle/Staffel gesetzt. Backend liest das für die Freischaltung
    // der gewählten Infopakete (Contract: siehe ACCESS_PACKAGE_SELECTION_AXEL.md).
    ...(l.selectedPackages?.length
      ? { selected_packages: l.selectedPackages.map((p) => p.package_id) }
      : {}),
  }))
}

async function parseCheckoutError(res: Response, fallback: string): Promise<string> {
  let msg = fallback
  try {
    const j = await res.json()
    if (j?.detail) msg = j.detail
  } catch {
    /* ignore */
  }
  return msg
}

export async function getCatalog(): Promise<CatalogItem[]> {
  const res = await fetch(url("/billing/catalog"))
  if (res.status === 503) throw new Error("Billing ist derzeit nicht aktiv.")
  if (!res.ok) throw new Error(`Katalog konnte nicht geladen werden (${res.status}).`)
  const data = await res.json()
  return (data.items ?? []) as CatalogItem[]
}

export async function getMe(): Promise<Me> {
  const res = await fetch(url("/billing/me"), { headers: await authHeaders() })
  if (!res.ok) throw new Error(`Kontostatus konnte nicht geladen werden (${res.status}).`)
  return (await res.json()) as Me
}

export async function startCheckout(
  lines: CartLine[],
  method: PaymentMethod,
): Promise<CheckoutResponse> {
  const body = { items: itemsPayload(lines), method }
  const res = await fetch(url("/billing/checkout"), {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(body),
  })
  if (res.status === 401) throw new Error("Bitte zuerst anmelden.")
  if (!res.ok) throw new Error(await parseCheckoutError(res, `Checkout fehlgeschlagen (${res.status}).`))
  return (await res.json()) as CheckoutResponse
}

// Gast-Checkout (ohne Login): NUR Karte. KEIN Authorization-Header — die Route
// ist bewusst auth-frei; Stripe erfasst die E-Mail, der Webhook legt danach das
// Konto an. Serverseitig auf reinen Karten-Checkout festgelegt (keine Rechnung).
export async function startGuestCheckout(lines: CartLine[]): Promise<CheckoutResponse> {
  const body = { items: itemsPayload(lines), method: "checkout" }
  const res = await fetch(url("/billing/guest-checkout"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(await parseCheckoutError(res, `Checkout fehlgeschlagen (${res.status}).`))
  return (await res.json()) as CheckoutResponse
}

export interface GuestBillingInput {
  email: string
  company: string
  street: string
  postal_code: string
  city: string
  country?: string
  ust_id?: string
  note?: string
}

// Gast-Rechnungsanfrage (Neukunde ohne Login, "auf Rechnung"). KEIN Auth-Header;
// Route ist auth-frei. Es entsteht KEIN Konto/Zugang, bis die Buchhaltung die
// Rechnung im Admin freigibt (finalize+send).
export async function startGuestInvoice(
  lines: CartLine[],
  billing: GuestBillingInput,
): Promise<{ ok: boolean; message?: string }> {
  const body = { items: itemsPayload(lines), ...billing, country: billing.country || "DE" }
  const res = await fetch(url("/billing/guest-invoice"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(await parseCheckoutError(res, `Rechnungsanfrage fehlgeschlagen (${res.status}).`))
  return (await res.json()) as { ok: boolean; message?: string }
}

export async function openPortal(): Promise<string | null> {
  const res = await fetch(url("/billing/portal"), {
    method: "POST",
    headers: await authHeaders(),
  })
  if (!res.ok) throw new Error(`Rechnungsportal nicht verfuegbar (${res.status}).`)
  const data = await res.json()
  return data.url ?? null
}
